import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import produce from 'immer'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'

import { useToastContext } from '../../../../../contexts/toast'
import {
  Invoice,
  InvoiceItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import { isItemInitial } from '../../../../../utils/helper/checkConfirmationCodes'
import {
  ConfigurableComponent,
  ConfigurableTable,
  convertOrderItems,
} from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config/context'
import {
  requestCompleteInvoiceById,
  useAllInvoiceItems,
  useInvoiceById,
} from '../../../data'

type Props = {
  visible: boolean
  setVisible: (params: boolean) => void
  invoiceId: string
}

export const AcceptInvoice: React.FC<Props> = ({
  visible,
  setVisible,
  invoiceId,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const navigate = useNavigate()

  const { data: invoice } = useInvoiceById(invoiceId)
  const { data: invoiceItems, isLoading } = useAllInvoiceItems(invoiceId)

  const { uiConfig: config } = useConfigContext()
  const { showToast } = useToastContext()

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  const currency = invoice?.Payload.OrderCurrency

  const currentDate = new Date()

  const prepareCompletePatchDto = (
    newInvoice: WebEdiDto<Invoice>,
    oldInvoice: WebEdiDto<Invoice>,
  ): PatchRequest<WebEdiDto<Invoice>> => {
    const patchDtos: PatchRequest<WebEdiDto<Invoice>> = []

    // ConfirmationCode
    patchDtos.push({
      op: oldInvoice.Payload.ConfirmationCode ? 'replace' : 'add',
      path: '/Payload/ConfirmationCode',
      value: newInvoice.Payload.ConfirmationCode,
    })

    // ConfirmationDate
    if (
      newInvoice.Payload.ConfirmationDate !==
      oldInvoice.Payload.ConfirmationDate
    ) {
      patchDtos.push({
        op: oldInvoice.Payload.ConfirmationDate ? 'replace' : 'add',
        path: '/Payload/ConfirmationDate',
        value: newInvoice.Payload.ConfirmationDate,
      })
    }

    // InvoiceDate
    if (newInvoice.Payload.InvoiceDate !== oldInvoice.Payload.InvoiceDate) {
      patchDtos.push({
        op: oldInvoice.Payload.InvoiceDate ? 'replace' : 'add',
        path: '/Payload/InvoiceDate',
        value: newInvoice.Payload.InvoiceDate,
      })
    }

    return patchDtos
  }

  async function onSubmit() {
    // deactivate the buttons
    setIsRequestRunning(true)

    try {
      // close the dialog and reset
      handleClose()

      // submit form
      await submitForm()

      // navigate back to the start page
      navigate('/')

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t('invoice.title.send.dialog.send.messages.success.summary', {
          orderId: invoice?.Payload.OrderId,
        }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  // submit form
  const submitForm = () => {
    // make a copy of despatchAdvice
    const newInvoice = produce(invoice!, draft => {
      draft.Payload.ConfirmationDate = new Date().toISOString() // set date to now

      draft.Payload.InvoiceDate = currentDate.toISOString()

      // check if an item was modified or rejected
      const isItemModified = (item: InvoiceItem) =>
        item.ConfirmationCode === 'MODIFIED' ||
        item.ConfirmationCode === 'REJECTED'

      draft.Payload.ConfirmationCode = invoiceItems?.some(isItemModified)
        ? 'MODIFIED'
        : 'ACCEPTED'
    })

    // patch for invoice
    const response = requestCompleteInvoiceById(
      invoiceId,
      prepareCompletePatchDto(newInvoice, invoice!),
    )

    // return response header
    return response
  }

  // handleClose
  const handleClose = () => {
    setVisible(false)
  }

  // ************************************************************
  // components
  // ************************************************************
  // InvoiceNumber
  const invoiceNumberLabel = (
    <label htmlFor="invoiceNumber" className="text-sm"></label>
  )

  const invoiceNumberComponent = (
    <InputText
      id="invoiceNumber"
      value={invoice?.Payload.InvoiceNumber}
      className="w-full"
      disabled
    />
  )

  const invoiceNumberWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={invoiceNumberComponent}
      label={invoiceNumberLabel}
    />
  )

  // InvoiceDate
  const invoiceDateLabel = (
    <label htmlFor="invoiceDate" className="text-sm"></label>
  )

  const invoiceDateComponent = (
    <Calendar
      id="invoiceDate"
      value={currentDate}
      className="w-full"
      readOnlyInput
      locale={language}
      disabled
    />
  )

  const invoiceDateWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={invoiceDateComponent}
      label={invoiceDateLabel}
    />
  )

  // ConfirmationTotalNetItemAmount
  const confirmationTotalNetItemAmountLabel = (
    <label htmlFor="confirmationTotalNetItemAmount" className="text-sm"></label>
  )

  const confirmationTotalNetItemAmountComponent = (
    <InputNumber
      id="confirmationTotalNetItemAmount"
      value={invoice?.Payload.ConfirmationTotalNetItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationTotalNetItemAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationTotalNetItemAmountComponent}
      label={confirmationTotalNetItemAmountLabel}
    />
  )

  // ConfirmationSurcharge
  const confirmationSurchargeLabel = (
    <label htmlFor="confirmationSurcharge" className="text-sm"></label>
  )

  const confirmationSurchargeComponent = (
    <InputNumber
      id="confirmationSurcharge"
      value={invoice?.Payload.ConfirmationSurcharge}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationSurchargeWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationSurchargeComponent}
      label={confirmationSurchargeLabel}
    />
  )

  // confirmationNetAmount
  const confirmationNetAmountLabel = (
    <label htmlFor="confirmationNetAmount" className="text-sm"></label>
  )

  const confirmationNetAmountComponent = (
    <InputNumber
      id="confirmationNetAmount"
      value={invoice?.Payload.ConfirmationNetAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationNetAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationNetAmountComponent}
      label={confirmationNetAmountLabel}
    />
  )

  // confirmationTaxRate
  const confirmationTaxRateLabel = (
    <label htmlFor="confirmationTaxRate" className="text-sm"></label>
  )

  const confirmationTaxRateComponent = (
    <InputNumber
      id="confirmationTaxRate"
      value={invoice?.Payload.ConfirmationTaxRate}
      className="w-full"
      disabled
      locale={language}
      suffix=" %"
    />
  )

  const confirmationTaxRateWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationTaxRateComponent}
      label={confirmationTaxRateLabel}
    />
  )

  // confirmationTaxValue
  const confirmationTaxValueLabel = (
    <label htmlFor="confirmationTaxValue" className="text-sm"></label>
  )

  const confirmationTaxValueComponent = (
    <InputNumber
      id="confirmationTaxValue"
      value={invoice?.Payload.ConfirmationTaxValue}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationTaxValueWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationTaxValueComponent}
      label={confirmationTaxValueLabel}
    />
  )

  // confirmationInvoiceAmount
  const confirmationInvoiceAmountLabel = (
    <label htmlFor="confirmationInvoiceAmount" className="text-sm"></label>
  )

  const confirmationInvoiceAmountComponent = (
    <InputNumber
      id="confirmationInvoiceAmount"
      value={invoice?.Payload.ConfirmationInvoiceAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationInvoiceAmountWrapper = (
    <ConfigurableComponent
      config={config.invoice.acceptInvoice}
      component={confirmationInvoiceAmountComponent}
      label={confirmationInvoiceAmountLabel}
    />
  )

  // ************************************************************
  // elements
  // ************************************************************
  // dialog
  const header = <>{t('invoice.title.send.dialog.title')}</>

  const footer = (
    <>
      <Button
        label={t(`common.buttons.cancel`)}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t(`common.buttons.send`)}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={onSubmit}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '60rem' }}
        breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12 md:col-4">{invoiceNumberWrapper}</div>
          <div className="col-12 md:col-4">{invoiceDateWrapper}</div>
        </div>

        <div className="formgrid grid">
          <div className="col-12 md:col-4">
            {confirmationTotalNetItemAmountWrapper}
          </div>
          <div className="col-12 md:col-4">{confirmationSurchargeWrapper}</div>
          <div className="col-12 md:col-4">{confirmationNetAmountWrapper}</div>
          <div className="col-12 md:col-4">{confirmationTaxRateWrapper}</div>
          <div className="col-12 md:col-4">{confirmationTaxValueWrapper}</div>
          <div className="col-12 md:col-4">
            {confirmationInvoiceAmountWrapper}
          </div>
        </div>

        <h3>{t('invoice.title.send.dialog.table.title')}</h3>
        {isItemInitial(invoiceItems!) && (
          <Message
            className="mb-3"
            text={t('invoice.title.send.dialog.table.message')}
          />
        )}

        <ConfigurableTable
          config={config.invoice.acceptInvoice.table}
          data={convertOrderItems(invoiceItems)}
          isLoading={isLoading}
          stateKey=""
        />
      </Dialog>
    </>
  )
}
